/**
 * m3 echo Frontend - Identity isolation
 *
 * Cached responses belong to the identity that fetched them, and the identity
 * can change without a logout and without a full page reload. Every one of
 * those goes through `setAuth()`: a second login on the login page, the SSO
 * callback, a session restore that comes back as somebody else. The logout in
 * `UserMenu` is the only switch that was ever explicitly cleaned up.
 *
 * Dropping the cache from a `useEffect` was one render too late. `set()`
 * publishes the new user synchronously, React then re-renders the tree with it,
 * and effects only run once that render has committed - so a query mounted
 * during that render could still be handed the previous identity's data out of
 * the cache. Query keys that carry the identity scope close most of that
 * window, but not the call sites still on literal keys, and not an entry that
 * was written before its key gained a scope segment.
 *
 * The subscription below leaves no window at all. It runs inside `set()`, i.e.
 * before React is told anything changed: it is registered at module scope,
 * before the first component has mounted, and zustand notifies its listeners in
 * registration order, so the cache is already empty by the time anything
 * observes the new identity.
 *
 * `queryClient.clear()` and not the async `resetQueryCache()`, for exactly that
 * reason - awaiting the cancellation first is what re-opens the window.
 * Clearing is not the weaker of the two either: removing a query destroys it,
 * and destroying it cancels the fetch it has in flight, so no answer to a
 * request the previous identity made can still land in the cache.
 */

import { useEffect } from 'react';
import {
  getIdentityScope,
  useAuthStore,
  useIdentityScope,
  useIsAuthenticated,
  useMustChangePassword,
} from '../stores/useAuthStore';
import { queryClient } from '../utils/queryClient';
import websocketService from '../services/websocket';

/** The active identity's cache scope, or null while nobody is signed in. */
const currentIdentity = (): string | null =>
  useAuthStore.getState().user ? getIdentityScope() : null;

let lastIdentity: string | null = currentIdentity();

useAuthStore.subscribe(() => {
  const next = currentIdentity();
  if (next === lastIdentity) return;

  const previous = lastIdentity;
  lastIdentity = next;

  // A first login starts from an empty cache and an unopened socket. Only
  // transitions away from an identity have anything to clean up.
  if (previous === null) return;

  queryClient.clear();

  // The socket authenticated as the previous user and is subscribed to that
  // user's jobs. Closing it here rather than leaving it to the effect below
  // means it cannot deliver one more update in the meantime; the effect
  // reopens it for the new identity on the next commit.
  websocketService.disconnect();
});

/**
 * Keeps the connection and the cache tied to whoever is currently signed in.
 *
 * Mounted once, at the root of the tree.
 */
export const useIdentityIsolation = (): void => {
  const identityScope = useIdentityScope();
  const isAuthenticated = useIsAuthenticated();
  const mustChangePassword = useMustChangePassword();

  /**
   * One connection per identity. `identityScope` belongs in the dependency
   * list because neither `isAuthenticated` nor `mustChangePassword` changes
   * when the person behind them does: a switch without a reload used to leave
   * the previous user's socket open, authenticated as them and subscribed to
   * their tenant's jobs.
   */
  useEffect(() => {
    if (!isAuthenticated || mustChangePassword) return;

    console.log(`[App] Opening WebSocket connection for ${identityScope}...`);
    websocketService.connect();

    return () => {
      console.log('[App] Disconnecting WebSocket...');
      websocketService.disconnect();
    };
  }, [isAuthenticated, mustChangePassword, identityScope]);
};
